import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  EuiButton,
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPage,
  EuiPageBody,
  EuiSpacer,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { useAnketaI18n } from "src/internationalization";
import { useApi } from "src/api";
import { useOpsanioCan } from "../can";
import { AnketaResourcesEnum, useAnketaAuth } from "src/auth";
import { useToast } from "src/notifications";
import {
  GitLabMerge,
  OpsanioAssemblySite,
  OpsanioVersions,
  OpsanioWaves,
} from "src/questionnaires";
import { defaultInitWave, OPSANIO_WAVES } from "../waves/consts";
import { OpsanioApplianceFlyout } from "./flyout";
import { Catalog } from "anketa-core/types";
import { CreateQuestionnaire } from "src/questionnaires/create";
import AppliancesTable from "./table/table";
import { TableSkeleton } from "src/components/skeleton";
import { OpsanioApplianceOverview } from "./overview";
import {
  ApplianceFilters,
  ApplianceFilterState,
  defaultApplianceFilters,
  filterAppliances,
  statusCounts,
} from "./filters";
import { relativeLabel } from "./format";
import { useFleetHealth } from "./useFleetHealth";

export interface OpsanioApplianceData {
  id: string;
  createdAt: string;
  updatedAt: string;
  catalogId: string;
  createdBy: string;
  updatedBy: string;
  displayName: string;
  //OpsanioValues unparsed;
  content: any;
  common: OpsanioAssemblySite;
  wave: OpsanioWaves;
  archived?: boolean;
}
export const OpsanioApplianceDashboard = ({
  updateCatalog,
}: {
  updateCatalog: (c: Catalog) => void;
}): JSX.Element => {
  const { translate } = useAnketaI18n();
  const { Get, Put } = useApi();
  const can = useOpsanioCan();
  const { keycloak } = useAnketaAuth();
  const { addToast } = useToast();

  const [versions, setVersions] = useState<
    Record<OpsanioWaves, OpsanioVersions | null>
  >({
    dev: null,
    main: null,
    qa: null,
    wave1: null,
    wave2: null,
    wave3: null,
    debug: null,
  });

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tags, setTags] = useState({});
  const [merges, setMerges] =
    useState<Record<OpsanioWaves, GitLabMerge | null>>(null);
  const [appliances, setAppliances] =
    useState<Array<OpsanioApplianceData> | null>(null);
  const [selectedItem, setSelectedItem] = useState<OpsanioApplianceData | null>(
    null,
  );
  const [modal, setModal] = useState<JSX.Element | null>(null);
  const [filters, setFilters] = useState<ApplianceFilterState>(
    defaultApplianceFilters,
  );
  const i18nPrefix = "anketa.opsanio.appliance";

  // One filter state drives the health overview charts, the status filter and
  // the table. The PRTG/integration sweep is owned here - one sweep feeds the
  // overview aggregates, the table's live columns and the status filter, instead
  // of each surface fanning out on its own.
  const allAppliances = appliances ?? [];
  const health = useFleetHealth(allAppliances);
  const filteredAppliances = useMemo(
    () => filterAppliances(allAppliances, filters, health.byId),
    [allAppliances, filters, health.byId],
  );

  const canFetch = useMemo<boolean>(() => {
    if (keycloak) {
      if (typeof keycloak.token === "string") {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }, [keycloak]);

  const loadAllVersions = async (
    updateLoadingState: boolean = false,
  ): Promise<void> => {
    if (canFetch) {
      if (updateLoadingState) {
        setError(null);
      }
      const loadedVersions: Record<string, OpsanioVersions | null> = {};

      // Waves are independent rows - load them concurrently, not one by one.
      await Promise.all(
        OPSANIO_WAVES.map(async (wave) => {
          try {
            const { data, error, status } = await Get({
              path: `v2/${AnketaResourcesEnum.opsanio}/versions/${wave}`,
              responseType: "json",
              token: keycloak.token,
            });
            if (error && status !== 404) {
              addToast({
                title: translate("anketa.opsanio.waves.fetch.error.title", {
                  defaultMessage: "Fehler beim pullen der Versionen!",
                }),
                color: "danger",
                text: error.message,
              });
            }
            // Seed defaults ONLY when the wave has no row yet (404). Any
            // other failure (500, timeout, 403) must not overwrite the real
            // pinned versions - it just leaves this wave unloaded.
            loadedVersions[wave] = null;
            if (data !== null) {
              loadedVersions[wave] = data as OpsanioVersions;
            } else if (
              status === 404 &&
              can(AnketaResourcesEnum.opsanioUpdates, "update")
            ) {
              switch (wave) {
                case "dev":
                  loadedVersions[wave] = {
                    ...defaultInitWave,
                    parentWave: "dev",
                    opsanio_plugin_version: "test",
                    pullPolicy: "Always",
                    coredns_version: "test",
                    codex: "test",
                    imap_wodle: "test",
                    kubernetes_wodle: "test",
                    osp_wodle: "test",
                    promtail_wodle: "test",
                    remotelog_wodle: "test",
                    semaphrone: "test",
                    snmp_wodle: "test",
                    opsanio_plugin_tag: `${loadedVersions[wave]?.opensearch_dashboard_version}-${loadedVersions[wave]?.wazuh_version}-test`,
                    wazuh_rules: "test",
                    webhook_wodle: "test",
                    netflow_wodle: "test",
                    most: "test",
                    cardea: "test",
                    apt_ring: "dev",
                  };
                  break;
                case "main":
                  loadedVersions[wave] = {
                    ...defaultInitWave,
                    pullPolicy: "Always",
                    parentWave: "dev",
                    opsanio_plugin_version: "1.12.0",
                    coredns_version: "latest",
                    codex: "latest",
                    opsanio_plugin_tag: `${loadedVersions[wave]?.opensearch_dashboard_version}-${loadedVersions[wave]?.wazuh_version}-1.12.0`,
                    imap_wodle: "latest",
                    kubernetes_wodle: "latest",
                    osp_wodle: "latest",
                    promtail_wodle: "latest",
                    remotelog_wodle: "latest",
                    semaphrone: "latest",
                    snmp_wodle: "latest",
                    wazuh_rules: "latest",
                    webhook_wodle: "latest",
                    netflow_wodle: "latest",
                    most: "latest",
                    cardea: "latest",
                    apt_ring: "",
                  };
                  break;
                case "qa":
                case "wave1":
                case "wave2":
                case "wave3":
                  loadedVersions[wave] = {
                    ...defaultInitWave,
                    parentWave:
                      wave === "qa"
                        ? "main"
                        : (OPSANIO_WAVES[
                            OPSANIO_WAVES.indexOf(wave) - 1
                          ] as OpsanioWaves),
                    pullPolicy: "IfNotPresent",
                    opsanio_plugin_version: "1.11.4",
                    coredns_version: "latest",
                    opsanio_plugin_tag: `${loadedVersions[wave]?.opensearch_dashboard_version}-${loadedVersions[wave]?.wazuh_version}-1.11.4`,
                    codex: "v1.0.26",
                    most: "v1.0.15",
                    cardea: "v1.1.9",
                    apt_ring: "",
                    semaphrone: "v1.0.21",
                    wazuh_rules: "v1.0.4",
                    imap_wodle: "v1.0.22",
                    kubernetes_wodle: "v1.0.20",
                    netflow_wodle: "v1.0.22",
                    osp_wodle: "v1.0.76",
                    promtail_wodle: "v1.0.18",
                    remotelog_wodle: "v1.0.19",
                    snmp_wodle: "v1.0.21",
                    webhook_wodle: "v1.0.22",
                  };
                  break;
                default:
                  loadedVersions[wave] = null;
                  break;
              }

              if (loadedVersions[wave]) {
                const { error: postError } = await Put({
                  path: `v2/${AnketaResourcesEnum.opsanio}/versions/${wave}`,
                  value: loadedVersions[wave],
                  token: keycloak.token,
                });
                if (postError) {
                  console.error(postError);
                  addToast({
                    title: translate("anketa.opsanio.waves.post.error.title", {
                      defaultMessage:
                        "Fehler beim initialen anlegen der Versionen!",
                    }),
                    color: "danger",
                    text: postError.message,
                  });
                }
              }
            }
          } catch (err) {
            console.error(`Error loading ${wave}:`, err);
            loadedVersions[wave] = null;
          }
        }),
      );

      setVersions(
        loadedVersions as Record<OpsanioWaves, OpsanioVersions | null>,
      );
    }
  };

  const getTags = async (updateLoadingState: boolean = false) => {
    if (canFetch) {
      if (updateLoadingState) {
        setError(null);
      }
      const { data, error } = await Get({
        path: `v2/${AnketaResourcesEnum.opsanio}/gitlab/tags`,
        responseType: "json",
        token: keycloak.token,
      });
      if (error) {
        addToast({
          title: translate("anketa.opsanio.gitlab.toke.fetch.error.title", {
            defaultMessage: "Fehler beim pullen der Tags!",
          }),
          color: "danger",
          text: error.message,
        });
      }
      setTags(data);
    }
  };

  const getMerges = async (updateLoadingState: boolean = false) => {
    if (canFetch) {
      if (updateLoadingState) {
        setError(null);
      }
      const { data, error } = await Get({
        path: `v2/${AnketaResourcesEnum.opsanio}/gitlab/flux/merge`,
        responseType: "json",
        token: keycloak.token,
      });
      if (error) {
        addToast({
          title: translate("anketa.opsanio.gitlab.merge.fetch.error.title", {
            defaultMessage: "Fehler beim pullen der Merges!",
          }),
          color: "danger",
          text: error.message,
        });
      }
      // null means "still loading" to the timeline. Once the request is done
      // - failed, or no open merge requests (empty answer) - hand it an empty
      // record so it renders instead of spinning forever.
      setMerges((data ?? {}) as Record<OpsanioWaves, GitLabMerge | null>);
    }
  };

  const getAppliances = async (updateLoadingState: boolean = false) => {
    if (canFetch) {
      if (updateLoadingState) {
        setLoading(true);
        setError(null);
      }
      const { data, error } = await Get({
        path: `v2/${AnketaResourcesEnum.opsanio}/appliances/summary`,
        responseType: "json",
        token: keycloak.token,
      });
      if (error) {
        // Also drive the inline error+retry state, not just a transient toast.
        setError(error.message);
        addToast({
          title: translate(`${i18nPrefix}.fetch.error`),
          color: "danger",
          text: error.message,
        });
      }
      setAppliances(data as Array<OpsanioApplianceData>);
      if (updateLoadingState) {
        setLoading(false);
      }
    }
  };

  // Versions, module tags and flux merges only feed the flyout's wave tab, and
  // tags + merges are GitLab round-trips - so they are loaded the first time
  // that tab opens instead of holding up the fleet overview on every visit.
  const waveDataRequested = useRef<boolean>(false);
  const [waveDataReady, setWaveDataReady] = useState<boolean>(false);

  const loadWaveData = async () => {
    if (canFetch) {
      waveDataRequested.current = true;
      await Promise.all([loadAllVersions(), getTags(), getMerges()]);
      setWaveDataReady(true);
    }
  };

  const ensureWaveData = () => {
    if (!waveDataRequested.current) {
      loadWaveData();
    }
  };

  const loadAllData = async (updateLoadingState: boolean = false) => {
    if (canFetch) {
      if (updateLoadingState) {
        setLoading(true);
        setError(null);
      }
      // Independent fetches; run them concurrently so the caller waits on the
      // slowest one rather than the sum. Wave data is only refreshed once it
      // has been requested - nobody is looking at it otherwise.
      await Promise.all([
        getAppliances(),
        ...(waveDataRequested.current ? [loadWaveData()] : []),
      ]);
      if (updateLoadingState) {
        setLoading(false);
      }
    }
  };

  const onFlyoutClose = () => {
    setSelectedItem(null);
  };

  const updateSelectedItem = (item: OpsanioApplianceData) => {
    if (selectedItem !== null && selectedItem.id === item.id) {
      setSelectedItem(null);
    }
    setSelectedItem(item);
  };
  useEffect(() => {
    loadAllData(true);
  }, [canFetch]);

  if (loading) {
    return (
      <EuiPage paddingSize="l">
        <EuiPageBody>
          <TableSkeleton rows={8} />
        </EuiPageBody>
      </EuiPage>
    );
  }

  // Prev/next in the flyout walks the table's current (filtered) order.
  const selectedIndex =
    selectedItem !== null
      ? filteredAppliances.findIndex((a) => a.id === selectedItem.id)
      : -1;
  const navigateFlyout = (delta: number) => {
    if (selectedIndex < 0) return;
    const next = filteredAppliances[selectedIndex + delta];
    if (next) setSelectedItem(next);
  };

  const counts = statusCounts(allAppliances, filters, health.byId);
  const activeCount = allAppliances.filter((a) => !a.archived).length;

  return (
    <EuiPage>
      <EuiPageBody>
        <EuiFlexGroup direction="column" gutterSize="m">
          {/* ---- Page header: what this page is, fleet summary, actions ---- */}
          <EuiFlexItem>
            <EuiFlexGroup alignItems="flexEnd" gutterSize="m" wrap responsive={false}>
              <EuiFlexItem>
                <EuiTitle size="l">
                  <h1>
                    {translate(`${i18nPrefix}.page.title`, {
                      defaultMessage: "Appliances",
                    })}
                  </h1>
                </EuiTitle>
                <EuiText size="s" color="subdued">
                  {health.loading && !health.loaded
                    ? translate(`${i18nPrefix}.page.subtitle.loading`, {
                        values: { total: allAppliances.length, active: activeCount },
                        defaultMessage:
                          "{{total}} appliances · {{active}} active · loading live health…",
                      })
                    : translate(`${i18nPrefix}.page.subtitle`, {
                        values: {
                          total: allAppliances.length,
                          active: activeCount,
                          sync: relativeLabel(health.fleet.lastSync, translate),
                        },
                        defaultMessage:
                          "{{total}} appliances · {{active}} active · health sync {{sync}}",
                      })}
                </EuiText>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiButton
                  iconType="refresh"
                  onClick={health.run}
                  isLoading={health.loading}
                >
                  {translate("anketa.opsanio.overview.refresh", {
                    defaultMessage: "Refresh health",
                  })}
                </EuiButton>
              </EuiFlexItem>
              {/* Server: questionnaires · create, internal-only. */}
              {can(AnketaResourcesEnum.questionnaires, "create", {
                isInternal: true,
              }) && (
              <EuiFlexItem grow={false}>
                <EuiButton
                  fill
                  iconType="plus"
                  onClick={() => {
                    setModal(
                      <CreateQuestionnaire
                        onClose={() => {
                          setModal(null);
                        }}
                        updateCatalog={null}
                      />,
                    );
                  }}
                >
                  {translate("anketa.create", {
                    values: {
                      type: translate("anketa.questionnaire"),
                    },
                  })}
                </EuiButton>
              </EuiFlexItem>
              )}
            </EuiFlexGroup>
          </EuiFlexItem>

          {error && (
            <EuiFlexItem>
              <EuiCallOut
                title={translate(`${i18nPrefix}.loading.error`)}
                color="danger"
                iconType="alert"
              >
                <p>{error}</p>
                <EuiButton
                  color="danger"
                  iconType="refresh"
                  size="s"
                  onClick={() => loadAllData(true)}
                >
                  {translate("anketa.common.retry", {
                    defaultMessage: "Retry",
                  })}
                </EuiButton>
              </EuiCallOut>
              <EuiSpacer />
            </EuiFlexItem>
          )}

          <EuiFlexItem>
            <OpsanioApplianceOverview
              filtered={filteredAppliances}
              health={health}
              onShowAttention={() =>
                setFilters({ ...filters, status: "attention" })
              }
            />
          </EuiFlexItem>

          <EuiFlexItem>
            <AppliancesTable
              appliances={filteredAppliances}
              loading={loading}
              updateSelectedItem={updateSelectedItem}
              reload={() => getAppliances(true)}
              healthById={health.byId}
              healthLoading={health.loading}
              selectedId={selectedItem?.id ?? null}
              renderToolbar={(columnSetSwitch) => (
                <ApplianceFilters
                  appliances={allAppliances}
                  filters={filters}
                  onChange={setFilters}
                  shown={filteredAppliances.length}
                  total={allAppliances.length}
                  counts={counts}
                  extra={columnSetSwitch}
                />
              )}
            />
          </EuiFlexItem>
        </EuiFlexGroup>

        {/* The flyout is an overlay - it no longer sits in a flex slot
            beside the table. */}
        {selectedItem !== null && (
          <OpsanioApplianceFlyout
            appliance={selectedItem}
            healthEntry={health.byId[selectedItem.id]}
            healthLoading={health.loading && !health.loaded}
            position={{
              index: selectedIndex,
              total: filteredAppliances.length,
            }}
            onNavigate={navigateFlyout}
            onClose={onFlyoutClose}
            loadAllData={loadAllData}
            onWaveTabOpen={ensureWaveData}
            merges={waveDataReady ? merges : null}
            tags={tags}
            versions={versions}
            updateCatalog={updateCatalog}
          />
        )}
        {modal !== null && modal}
      </EuiPageBody>
    </EuiPage>
  );
};
